import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import AppNavBar from './Components/AppNavBar';
import GameChromeNav from './Components/GameChromeNav';
import OnlineGameBanner from './Components/OnlineGameBanner';
import AuthProvider from './contexts/AuthContext';
import GameSessionProvider from './contexts/GameSessionContext';
import PlayModalProvider from './contexts/PlayModalContext';
import SnackBarProvider from './contexts/SnackBarContext';
import Community from './modules/Community/Community';
import GameRouter from './modules/Game/GameRouter';
import Home from './modules/Home/Home';
import SkinLab from './modules/Lab/SkinLab';
import Attributions from './modules/Legal/Attributions';
import Privacy from './modules/Legal/Privacy';
import Terms from './modules/Legal/Terms';
import Pass from './modules/Pass/Pass';
import PlayHub from './modules/Play/PlayHub';
import Queue from './modules/Queue/Queue';
import Profile from './modules/Profile/Profile';
import Rules from './modules/Rules/Rules';
import Settings from './modules/Settings/Settings';
import Store from './modules/Store/Store';
import StoreCategoryPage from './modules/Store/StoreCategoryPage';
import Leaderboard from './modules/Leaderboard/Leaderboard';

function isImmersivePath(pathname: string) {
  return pathname.startsWith('/play/') || pathname.startsWith('/queue/');
}

function AppShell() {
  const { pathname } = useLocation();
  const immersive = isImmersivePath(pathname);

  return (
    <div
      id="App"
      className="flex h-dvh max-h-dvh w-screen flex-col font-base text-bk-text pt-[env(safe-area-inset-top)]"
    >
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:z-50 focus:bg-bk-panel focus:px-4 focus:py-2 focus:text-bk-gold"
      >
        Skip to content
      </a>
      {immersive ? <GameChromeNav /> : <AppNavBar />}
      <OnlineGameBanner />
      <main
        id="main"
        className="bk-stage-bg relative min-h-0 w-full flex-1 overflow-y-auto text-sm lg:text-base pb-[env(safe-area-inset-bottom)]"
      >
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/play" element={<PlayHub />} />
          <Route path="/queue/:mode" element={<Queue />} />
          <Route path="/play/:gameId" element={<GameRouter />} />
          <Route path="/store" element={<Store />} />
          <Route path="/store/:category" element={<StoreCategoryPage />} />
          <Route path="/pass" element={<Pass />} />
          <Route path="/leaderboard" element={<Leaderboard />} />
          <Route path="/rules" element={<Rules />} />
          <Route path="/community" element={<Community />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/attributions" element={<Attributions />} />
          {import.meta.env.DEV ? (
            <Route path="/lab/skins" element={<SkinLab />} />
          ) : (
            <Route path="/lab/skins" element={<Navigate to="/" replace />} />
          )}
          <Route path="/settings" element={<Settings />} />
          <Route path="/user/:userId" element={<Profile />} />
        </Routes>
      </main>
    </div>
  );
}

function App() {
  return (
    <SnackBarProvider>
      <AuthProvider>
        <PlayModalProvider>
          <GameSessionProvider>
            <AppShell />
          </GameSessionProvider>
        </PlayModalProvider>
      </AuthProvider>
    </SnackBarProvider>
  );
}

export default App;
